/**
 * El PORQUÉ de cada ratio: por qué está en verde, ámbar o rojo AHORA.
 *
 * Un semáforo sin explicación obliga a saberse los umbrales de memoria. Aquí se
 * traduce el valor concreto a una frase que dice qué está pasando, que es lo que
 * hace útil la pantalla a quien no es contable.
 *
 * Los textos se portan de Ledgeria (src/lib/kpis.ts, `motivo`), donde llevan
 * tiempo usándose contra la contabilidad de la fundación. Los CORTES ya no se
 * repiten aquí: salen de `SEMAFORO_RULES` en @toga/shared, la misma tabla con
 * la que el backend decide el color. Esta función no decide el color, solo lo
 * explica, y antes lo explicaba con una copia a mano de los mismos números:
 * tocar uno y olvidar el otro dejaba la tarjeta diciendo «holgado» en rojo.
 *
 * Devuelve una CLAVE de i18n, no un literal, porque el CRM es trilingüe.
 */

import { SEMAFORO_RULES as R } from '@toga/shared'

/** Claves de ratio que emite el backend (`/accounting/kpis`). */
export type RatioKey =
  | 'liquidezCorriente'
  | 'pruebaAcida'
  | 'fondoManiobra'
  | 'endeudamiento'
  | 'calidadDeuda'
  | 'margenBrutoPct'
  | 'ebitdaMarginPct'
  | 'netMarginPct'
  | 'salesGrowthMoM'
  | 'salesGrowthYoY'

/**
 * Sufijo de la clave i18n para el motivo. Se resuelve como
 * `accounting:insight.<ratioKey>.<sufijo>`.
 *
 * `value` llega en la MISMA unidad que muestra la tarjeta: los ratios como
 * número (1,35), los porcentajes como porcentaje (34,5 → no 0,345) y el fondo
 * de maniobra en céntimos.
 */
export function ratioInsightKey(key: RatioKey, value: number | null): string {
  if (value === null || Number.isNaN(value)) return 'na'

  switch (key) {
    case 'liquidezCorriente':
      // Ojo al orden: por encima de 2,2 el ratio es ALTO, y eso no es "mejor"
      // sino recursos ociosos. Si se comprobara primero >= 1.5 se etiquetaría
      // como óptimo un balance con activo corriente parado.
      if (value > R.liquidezCorriente.maxGreen) return 'idle'
      if (value >= R.liquidezCorriente.minGreen) return 'optimal'
      if (value >= R.liquidezCorriente.minYellow) return 'tight'
      return 'risk'

    case 'pruebaAcida':
      if (value > R.pruebaAcida.maxGreen) return 'idle'
      if (value >= R.pruebaAcida.minGreen) return 'optimal'
      if (value >= R.pruebaAcida.minYellow) return 'tight'
      return 'risk'

    case 'fondoManiobra':
      return value > 0 ? 'positive' : 'negative'

    case 'endeudamiento':
      // Poca deuda NO es peligro: es capacidad de financiación sin usar. Por eso
      // el extremo bajo es "precaución" y solo el alto es riesgo.
      if (value > R.endeudamiento.extraYellow.max) return 'excess'
      if (value > R.endeudamiento.maxGreen) return 'high'
      if (value >= R.endeudamiento.minGreen) return 'balanced'
      if (value >= R.endeudamiento.minYellow) return 'conservative'
      return 'underleveraged'

    case 'calidadDeuda':
      if (value <= R.calidadDeuda.maxGreen) return 'good'
      if (value <= R.calidadDeuda.maxYellow) return 'balanced'
      return 'shortTermPressure'

    case 'margenBrutoPct':
      if (value >= R.margenBrutoPct.minGreen) return 'wide'
      if (value >= R.margenBrutoPct.minYellow) return 'acceptable'
      return 'narrow'

    case 'ebitdaMarginPct':
      // Perder dinero no es una banda del semáforo: es su propio mensaje.
      if (value < 0) return 'negative'
      if (value >= R.ebitdaMarginPct.minGreen) return 'solid'
      if (value >= R.ebitdaMarginPct.minYellow) return 'moderate'
      return 'thin'

    case 'netMarginPct':
      if (value < 0) return 'loss'
      if (value >= R.netMarginPct.minGreen) return 'good'
      if (value >= R.netMarginPct.minYellow) return 'tight'
      return 'thin'

    case 'salesGrowthMoM':
    case 'salesGrowthYoY':
      return value > 0 ? 'growing' : 'shrinking'
  }
}
